"use client";

import { useState, useRef, useEffect, useCallback } from "react";
import ReactMarkdown from "react-markdown";

interface Source {
  ref: number;
  doc_id: string;
  doc_title: string;
  page: string | number;
  text: string;
}

interface Message {
  role: "user" | "assistant";
  content: string;
  sessionId?: string;
  sources?: Source[];
}

interface ChatWidgetProps {
  fullscreen?: boolean;
  selectedDocId?: string;
  selectedDocTitle?: string;
}

// ── 20 cross-document questions (Danish + English) ────────────────────────────
const CROSS_DOC_QUESTIONS: Record<"da" | "en", string[]> = {
  da: [
    "Hvordan hænger klimaplanen sammen med de lokale erhvervsstrategier?",
    "Hvad er sammenhængen mellem bygningsrenovering og CO₂-reduktionsmål?",
    "Hvilke borgerinddragelsesinitiativer understøtter den grønne omstilling?",
    "Hvordan koordineres transport- og energiindsatserne på tværs af kommunen?",
    "Hvad er tidsplanen for at nå 70% CO₂-reduktion inden 2030?",
    "Hvilke tilskudsordninger gælder for både private og erhverv?",
    "Hvordan påvirker klimaindsatsen lokal biodiversitet og naturpleje?",
    "Hvad er kommunens strategi for grøn omstilling af den kommunale drift?",
    "Hvordan spiller vedvarende energi og varmeforsyning sammen i planen?",
    "Hvilke klimatilpasningsprojekter er planlagt ved kysten og i byerne?",
    "Hvad er rollerne for henholdsvis borgere, erhvervsliv og kommunen i klimaplanen?",
    "Hvordan måles og rapporteres fremskridtet mod klimamål løbende?",
    "Hvilke projekter kombinerer energibesparelser og bæredygtig mobilitet?",
    "Hvad sker der med klimaplanen efter 2030 — er der mål for 2050?",
    "Hvordan understøtter kommunen lokale klimafællesskaber og partnerskaber?",
    "Hvad er de vigtigste barrierer for den grønne omstilling i kommunen?",
    "Hvilke konkrete projekter er allerede i gang inden for klimaindsatsen?",
    "Hvordan prioriteres klimainvesteringer i kommunens budget?",
    "Hvad er planen for at reducere madspild og fremme bæredygtig kost?",
    "Hvordan bidrager skovrejsning og naturgenopretning til klimaneutralitet?",
  ],
  en: [
    "How does the climate plan relate to the local business strategy?",
    "What is the link between building renovation and CO₂ reduction targets?",
    "Which citizen engagement initiatives support the green transition?",
    "How are transport and energy efforts coordinated across the municipality?",
    "What is the timeline for achieving a 70% CO₂ reduction by 2030?",
    "Which subsidy schemes apply to both private individuals and businesses?",
    "How does the climate effort impact local biodiversity and nature conservation?",
    "What is the municipality's strategy for greening its own operations?",
    "How do renewable energy and district heating plans connect?",
    "Which climate adaptation projects are planned for coastal and urban areas?",
    "What are the roles of citizens, businesses, and the municipality in the climate plan?",
    "How is progress toward climate goals measured and reported?",
    "Which projects combine energy savings and sustainable mobility?",
    "What happens after 2030 — are there goals set for 2050?",
    "How does the municipality support local climate communities and partnerships?",
    "What are the key barriers to green transition in the municipality?",
    "Which concrete climate projects are already underway?",
    "How are climate investments prioritised in the municipal budget?",
    "What is the plan for reducing food waste and promoting sustainable diets?",
    "How do afforestation and nature restoration contribute to climate neutrality?",
  ],
};

function sampleCrossQuestions(lang: "da" | "en", n = 5): string[] {
  const pool = [...CROSS_DOC_QUESTIONS[lang]];
  for (let i = pool.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [pool[i], pool[j]] = [pool[j], pool[i]];
  }
  return pool.slice(0, n);
}

function generateUUID() {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    return (c === "x" ? r : (r & 0x3) | 0x8).toString(16);
  });
}

// ── Source Cards ──────────────────────────────────────────────────────────────
function SourceCards({ sources, language }: { sources: Source[]; language: "da" | "en" }) {
  const [expanded, setExpanded] = useState<number | null>(null);
  if (!sources.length) return null;
  const label = language === "en" ? "Sources" : "Kilder";
  return (
    <div className="mt-3 border-t border-gray-200 pt-2">
      <p className="text-xs font-semibold text-gray-500 mb-1.5 uppercase tracking-wide">
        📄 {label}
      </p>
      <div className="flex flex-col gap-1.5">
        {sources.map((s) => (
          <div
            key={s.ref}
            className="bg-white border border-gray-200 rounded-xl overflow-hidden"
          >
            <button
              className="w-full text-left px-3 py-2 flex items-center gap-2 hover:bg-gray-50 transition-colors"
              onClick={() => setExpanded(expanded === s.ref ? null : s.ref)}
            >
              <span className="text-xs font-mono text-gray-400 w-5 shrink-0">
                [{s.ref}]
              </span>
              <span className="text-xs font-medium text-gray-800 flex-1 truncate">
                {s.doc_title}
              </span>
              <span className="text-xs text-gray-400 shrink-0">
                {language === "en" ? "p." : "s."} {s.page}
              </span>
              <span className="text-gray-400 text-xs">{expanded === s.ref ? "▲" : "▼"}</span>
            </button>
            {expanded === s.ref && (
              <div className="px-3 pb-3 pt-0">
                <p className="text-xs text-gray-600 leading-relaxed whitespace-pre-wrap border-l-2 border-green-300 pl-2">
                  {s.text}
                </p>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

export default function ChatWidget({ fullscreen = false, selectedDocId, selectedDocTitle }: ChatWidgetProps) {
  const [open, setOpen] = useState(fullscreen);
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState("");
  const [sessionId, setSessionId] = useState<string>("");
  const [loading, setLoading] = useState(false);
  const [selectedText, setSelectedText] = useState<string>("");
  const [editingSelectedText, setEditingSelectedText] = useState(false);
  const [editableText, setEditableText] = useState("");
  const [gdprConsent, setGdprConsent] = useState<boolean | null>(null);
  const [language, setLanguage] = useState<"da" | "en">("da");
  interface DocGroup { doc_id: string; doc_title: string; questions: string[]; }
  const [docGroups, setDocGroups] = useState<DocGroup[]>([]);
  const [questionsLoading, setQuestionsLoading] = useState(false);
  const [toolboxOpen, setToolboxOpen] = useState(false);
  const [checkedDocs, setCheckedDocs] = useState<Set<string>>(new Set());
  const [crossQuestions, setCrossQuestions] = useState<string[]>(() => sampleCrossQuestions("da"));
  const [conversationMode, setConversationMode] = useState<"continuous" | "single">("continuous");
  const [docScope, setDocScope] = useState<"all" | "current">("all");

  // Floatable window state (non-fullscreen only)
  const [floatPos, setFloatPos] = useState<{ x: number; y: number } | null>(null);
  const [floatSize, setFloatSize] = useState({ w: 384, h: 520 });
  const dragState = useRef<{ startX: number; startY: number; origX: number; origY: number } | null>(null);
  const resizeState = useRef<{ startX: number; startY: number; origW: number; origH: number } | null>(null);
  const floatContainerRef = useRef<HTMLDivElement>(null);

  const [llmModel, setLlmModel] = useState("…");
  useEffect(() => {
    fetch("/api/system/config")
      .then((r) => r.json())
      .then((d) => { if (d.llm_model) setLlmModel(d.llm_model); })
      .catch(() => {});
  }, []);

  // If the selected document is cleared externally, fall back to "all" scope
  useEffect(() => {
    if (!selectedDocId) setDocScope("all");
  }, [selectedDocId]);

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  // Load GDPR consent from localStorage on mount
  useEffect(() => {
    const stored = localStorage.getItem("klimaportal_gdpr_consent");
    if (stored === "true") setGdprConsent(true);
    else if (stored === "false") setGdprConsent(false);
    else setGdprConsent(null);
  }, []);

  // Fetch AI-generated questions (grouped per document). force=true busts the cache.
  const fetchQuestions = useCallback(async (force = false) => {
    setQuestionsLoading(true);
    try {
      const url = `/api/chat/suggested-questions?language=${language}${force ? "&force=true" : ""}`;
      const res = await fetch(url);
      if (!res.ok) throw new Error("fetch failed");
      const data = await res.json();
      if (Array.isArray(data.groups) && data.groups.length > 0) {
        setDocGroups(data.groups);
        setCheckedDocs(new Set(data.groups.map((g: DocGroup) => g.doc_id)));
      }
    } catch {
      // Keep existing groups
    } finally {
      setQuestionsLoading(false);
    }
  }, [language]);

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      setQuestionsLoading(true);
      try {
        const res = await fetch(`/api/chat/suggested-questions?language=${language}`);
        if (!res.ok) throw new Error();
        const data = await res.json();
        if (!cancelled && Array.isArray(data.groups) && data.groups.length > 0) {
          setDocGroups(data.groups);
          setCheckedDocs(new Set(data.groups.map((g: DocGroup) => g.doc_id)));
        }
      } catch { /* fallback renders below */ } finally {
        if (!cancelled) setQuestionsLoading(false);
      }
    };
    run();
    return () => { cancelled = true; };
  }, [language]);

  const acceptGdpr = () => {
    localStorage.setItem("klimaportal_gdpr_consent", "true");
    setGdprConsent(true);
  };

  const declineGdpr = () => {
    localStorage.setItem("klimaportal_gdpr_consent", "false");
    setGdprConsent(false);
    setOpen(false);
  };

  useEffect(() => {
    if (!sessionId) setSessionId(generateUUID());
  }, [sessionId]);

  const prevMsgCount = useRef(0);
  useEffect(() => {
    if (messages.length !== prevMsgCount.current) {
      prevMsgCount.current = messages.length;
      messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }
  }, [messages]);

  // Fix missing spaces in PDF-extracted text.
  // Absolutely-positioned word spans get concatenated without spaces by the
  // browser's selection API. We re-insert spaces by splitting on camelCase /
  // PascalCase boundaries that appear in Danish compound words and by
  // collapsing repeated whitespace.
  const normalizePdfText = (raw: string): string => {
    return raw
      // Collapse real newlines / tabs to a single space first
      .replace(/[\r\n\t]+/g, " ")
      // Insert space where a lower-case (or digit) letter is immediately
      // followed by an upper-case letter — common when two words are joined
      .replace(/([a-zæøåéàü])(\p{Lu})/gu, "$1 $2")
      // Collapse multiple spaces
      .replace(/ {2,}/g, " ")
      .trim();
  };

  // Capture text selection from page
  useEffect(() => {
    const handleSelection = () => {
      const sel = window.getSelection();
      const raw = sel?.toString() ?? "";
      const text = normalizePdfText(raw);
      if (text && text.length > 10) setSelectedText(text);
    };
    document.addEventListener("mouseup", handleSelection);
    return () => document.removeEventListener("mouseup", handleSelection);
  }, []);

  const toggleDoc = (docId: string) => {
    setCheckedDocs((prev) => {
      const next = new Set(prev);
      if (next.has(docId)) next.delete(docId);
      else next.add(docId);
      return next;
    });
  };

  const allChecked = docGroups.length > 0 && checkedDocs.size === docGroups.length;
  const toggleAll = () => {
    if (allChecked) setCheckedDocs(new Set());
    else setCheckedDocs(new Set(docGroups.map((g) => g.doc_id)));
  };

  // Questions from checked documents only
  const visibleGroups = docGroups.filter((g) => checkedDocs.has(g.doc_id));

  const sendMessage = useCallback(async () => {
    if (!input.trim() || loading) return;

    const userMessage = input.trim();
    setInput("");
    setLoading(true);

    const newMessages: Message[] = [
      ...messages,
      { role: "user", content: userMessage + (selectedText ? `\n\n[Markeret tekst: "${selectedText.slice(0, 200)}"]` : "") },
      { role: "assistant", content: "" },
    ];
    setMessages(newMessages);
    const msgIndex = newMessages.length - 1;

    try {
      const response = await fetch(`/api/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          message: userMessage,
          session_id: sessionId,
          selected_text: selectedText || undefined,
          stream: true,
          language,
          doc_filter: docScope === "current" && selectedDocId ? selectedDocId : undefined,
          single_mode: conversationMode === "single",
        }),
      });

      if (!response.ok) throw new Error("API error");

      const reader = response.body?.getReader();
      const decoder = new TextDecoder();
      let fullText = "";

      while (reader) {
        const { done, value } = await reader.read();
        if (done) break;

        const chunk = decoder.decode(value);
        const lines = chunk.split("\n");

        for (const line of lines) {
          if (line.startsWith("data: ")) {
            const data = line.slice(6).trim();
            if (data === "[DONE]") break;
            try {
              const parsed = JSON.parse(data);
              if (parsed.token) {
                fullText += parsed.token.replace(/\\n/g, "\n");
                setMessages((prev) => {
                  const updated = [...prev];
                  updated[msgIndex] = { ...updated[msgIndex], content: fullText };
                  return updated;
                });
              }
              if (parsed.sources) {
                setMessages((prev) => {
                  const updated = [...prev];
                  updated[msgIndex] = { ...updated[msgIndex], sources: parsed.sources };
                  return updated;
                });
              }
              if (parsed.session_id && !sessionId) setSessionId(parsed.session_id);
            } catch {}
          }
        }
      }
    } catch {
      setMessages((prev) => {
        const updated = [...prev];
        updated[msgIndex] = {
          ...updated[msgIndex],
          content: language === "en"
            ? "⚠️ Sorry, an error occurred. Please try again."
            : "⚠️ Beklager, der opstod en fejl. Prøv igen.",
        };
        return updated;
      });
    } finally {
      setLoading(false);
      setSelectedText("");
      setEditingSelectedText(false);
    }
  }, [input, loading, messages, selectedText, sessionId, language]);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const clearChat = async () => {
    if (sessionId) {
      await fetch(`/api/chat/session/${sessionId}`, { method: "DELETE" });
    }
    setMessages([]);
    setSessionId(generateUUID());
  };

  // Re-sample cross questions when language changes or toolbox opens
  useEffect(() => {
    setCrossQuestions(sampleCrossQuestions(language));
  }, [language]);

  const reshuffleCross = () => setCrossQuestions(sampleCrossQuestions(language));

  // Drag the floating window by its header
  const startDrag = useCallback((e: React.MouseEvent) => {
    if (fullscreen) return;
    if ((e.target as HTMLElement).closest('button')) return;
    e.preventDefault();
    const rect = floatContainerRef.current?.getBoundingClientRect();
    const origX = rect?.left ?? (typeof window !== 'undefined' ? window.innerWidth - floatSize.w - 24 : 0);
    const origY = rect?.top  ?? (typeof window !== 'undefined' ? window.innerHeight - floatSize.h - 24 : 0);
    setFloatPos({ x: origX, y: origY });
    dragState.current = { startX: e.clientX, startY: e.clientY, origX, origY };
    const onMove = (ev: MouseEvent) => {
      if (!dragState.current) return;
      setFloatPos({
        x: Math.max(0, dragState.current.origX + ev.clientX - dragState.current.startX),
        y: Math.max(0, dragState.current.origY + ev.clientY - dragState.current.startY),
      });
    };
    const onUp = () => {
      dragState.current = null;
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  }, [fullscreen, floatSize.w, floatSize.h]);

  // Resize the floating window from the bottom-right handle
  const startResize = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    resizeState.current = { startX: e.clientX, startY: e.clientY, origW: floatSize.w, origH: floatSize.h };
    const onMove = (ev: MouseEvent) => {
      if (!resizeState.current) return;
      setFloatSize({
        w: Math.max(300, resizeState.current.origW + ev.clientX - resizeState.current.startX),
        h: Math.max(400, resizeState.current.origH + ev.clientY - resizeState.current.startY),
      });
    };
    const onUp = () => {
      resizeState.current = null;
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  }, [floatSize.w, floatSize.h]);

  // ── Question Toolbox ─────────────────────────────────────────────────────────
  const fallbackQuestions = language === "en"
    ? ["What is the climate plan?", "Grants for heat pumps?", "When will we be climate-neutral?"]
    : ["Hvad er klimaplanen?", "Tilskud til varmepumpe?", "Hvornår er vi klimaneutrale?"];

  const QuestionToolbox = () => (
    <div className="mt-4 w-full text-left">
      {/* Collapsible header */}
      <button
        onClick={() => setToolboxOpen((v) => !v)}
        className="w-full flex items-center justify-between px-3 py-2 rounded-xl border border-green-200 bg-green-50 hover:bg-green-100 transition-colors text-sm font-medium text-green-800"
      >
        <span className="flex items-center gap-2">
          💡
          {language === "en" ? "Suggested questions" : "Foreslåede spørgsmål"}
          {visibleGroups.length > 0 && (
            <span className="text-xs bg-green-200 text-green-800 rounded-full px-2 py-0.5 font-normal">
              {visibleGroups.reduce((s, g) => s + g.questions.length, 0)}
            </span>
          )}
        </span>
        <span className="text-green-600 text-xs">{toolboxOpen ? "▲" : "▼"}</span>
      </button>

      {toolboxOpen && (
        <div className="mt-2 border border-green-100 rounded-xl overflow-hidden bg-white shadow-sm">
          {/* Document checklist */}
          {docGroups.length > 0 && (
            <div className="border-b border-green-100 px-3 py-2 bg-green-50">
              <div className="flex items-center justify-between mb-1.5">
                <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                  {language === "en" ? "Filter by document" : "Filtrer efter dokument"}
                </span>
                <button
                  onClick={toggleAll}
                  className="text-xs text-green-700 hover:underline"
                >
                  {allChecked
                    ? (language === "en" ? "Deselect all" : "Fravælg alle")
                    : (language === "en" ? "Select all" : "Vælg alle")}
                </button>
              </div>
              <div className="space-y-1 max-h-36 overflow-y-auto pr-1">
                {docGroups.map((group) => (
                  <label
                    key={group.doc_id}
                    className="flex items-center gap-2 cursor-pointer group"
                  >
                    <input
                      type="checkbox"
                      checked={checkedDocs.has(group.doc_id)}
                      onChange={() => toggleDoc(group.doc_id)}
                      className="accent-green-700 w-3.5 h-3.5 flex-shrink-0"
                    />
                    <span className="text-xs text-gray-700 truncate group-hover:text-green-800" title={group.doc_title}>
                      📄 {group.doc_title}
                    </span>
                    <span className="ml-auto text-xs text-gray-400 flex-shrink-0">
                      {group.questions.length}
                    </span>
                  </label>
                ))}
              </div>
            </div>
          )}

          {/* Questions from checked docs */}
          <div className="p-3 space-y-3 max-h-48 overflow-y-auto">
            {questionsLoading && docGroups.length === 0 ? (
              <div className="flex flex-wrap gap-2">
                {Array.from({ length: 4 }).map((_, i) => (
                  <span key={i} className="text-xs px-3 py-1.5 rounded-full border border-green-100 bg-green-50 text-transparent animate-pulse select-none">
                    Henter spørgsmål...
                  </span>
                ))}
              </div>
            ) : visibleGroups.length > 0 ? (
              visibleGroups.map((group) => (
                <div key={group.doc_id}>
                  <p className="text-xs font-medium text-gray-400 mb-1.5 truncate" title={group.doc_title}>
                    📄 {group.doc_title}
                  </p>
                  <div className="flex flex-wrap gap-1.5">
                    {group.questions.map((q) => (
                      <button
                        key={q}
                        onClick={() => { setInput(q); setToolboxOpen(false); inputRef.current?.focus(); }}
                        className="text-xs px-3 py-1.5 rounded-full border border-green-200 text-green-700 hover:bg-green-50 transition-colors text-left"
                      >
                        {q}
                      </button>
                    ))}
                  </div>
                </div>
              ))
            ) : docGroups.length > 0 && checkedDocs.size === 0 ? (
              <p className="text-xs text-gray-400 text-center py-2">
                {language === "en" ? "Select at least one document above." : "Vælg mindst ét dokument ovenfor."}
              </p>
            ) : (
              <div className="flex flex-wrap gap-1.5">
                {fallbackQuestions.map((q) => (
                  <button
                    key={q}
                    onClick={() => { setInput(q); setToolboxOpen(false); inputRef.current?.focus(); }}
                    className="text-xs px-3 py-1.5 rounded-full border border-green-200 text-green-700 hover:bg-green-50 transition-colors"
                  >
                    {q}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Cross-document questions section */}
          <div className="border-t border-blue-100 bg-blue-50 px-3 py-2">
            <div className="flex items-center justify-between mb-1.5">
              <span className="text-xs font-semibold text-blue-700 uppercase tracking-wide">
                🔀 {language === "en" ? "Cross-document questions" : "Tværgående spørgsmål"}
              </span>
              <button
                onClick={reshuffleCross}
                className="text-xs text-blue-600 hover:underline"
                title={language === "en" ? "Show different questions" : "Vis andre spørgsmål"}
              >↺ {language === "en" ? "Shuffle" : "Bland"}</button>
            </div>
            <div className="flex flex-wrap gap-1.5">
              {crossQuestions.map((q) => (
                <button
                  key={q}
                  onClick={() => { setInput(q); setToolboxOpen(false); inputRef.current?.focus(); }}
                  className="text-xs px-3 py-1.5 rounded-full border border-blue-200 text-blue-700 hover:bg-blue-100 transition-colors text-left"
                >
                  {q}
                </button>
              ))}
            </div>
          </div>

          {/* Footer: refresh AI questions */}
          <div className="border-t border-green-100 px-3 py-2 flex items-center justify-between bg-green-50">
            <span className="text-xs text-gray-400">
              {!questionsLoading && docGroups.length > 0 && (language === "en" ? "✨ AI-generated" : "✨ AI-genereret")}
            </span>
            <button
              onClick={() => fetchQuestions(true)}
              disabled={questionsLoading}
              className="text-xs text-green-700 border border-green-200 rounded-full px-3 py-1 hover:bg-green-100 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            >
              {questionsLoading
                ? (language === "en" ? "⏳ Generating..." : "⏳ Genererer...")
                : (language === "en" ? "🔄 Regenerate" : "🔄 Generer nye")}
            </button>
          </div>
        </div>
      )}
    </div>
  );

  // Floating button (non-fullscreen mode)
  if (!fullscreen && !open) {
    return (
      <button
        onClick={() => setOpen(true)}
        className="fixed bottom-6 right-6 w-14 h-14 rounded-full text-white shadow-lg hover:scale-105 transition-transform z-50 flex items-center justify-center text-2xl"
        style={{ backgroundColor: "#2d6a4f" }}
        title="Spørg AI-assistenten"
      >
        💬
      </button>
    );
  }

  const chatContent = (
    <div className={`flex flex-col ${fullscreen ? "h-full min-h-[calc(100vh-8rem)]" : "h-full"} bg-white`}>
      {/* GDPR consent screen */}
      {gdprConsent !== true && (
        <div className="flex flex-col h-full justify-between p-5">
          <div>
            <div className="flex items-center gap-2 mb-4">
              <span className="text-2xl">🔒</span>
              <h2 className="font-bold text-gray-900 text-base">Databeskyttelse (GDPR)</h2>
            </div>
            <p className="text-sm text-gray-700 mb-3 leading-relaxed">
              Klimaassistenten gemmer dine spørgsmål og svar for at forbedre samtalekvaliteten.
              Din privacy er beskyttet på følgende måde:
            </p>
            <ul className="text-sm text-gray-700 space-y-2 mb-4">
              <li className="flex gap-2">
                <span className="text-green-600 flex-shrink-0">✅</span>
                <span><strong>Pseudonymiseret session-ID</strong> — dit ID omdannes til en SHA-256 hash. Den relle værdi gemmes aldrig.</span>
              </li>
              <li className="flex gap-2">
                <span className="text-green-600 flex-shrink-0">✅</span>
                <span><strong>PII-filtrering</strong> — e-mail, telefonnumre og CPR-numre fjernes automatisk inden lagring.</span>
              </li>
              <li className="flex gap-2">
                <span className="text-green-600 flex-shrink-0">✅</span>
                <span><strong>Kun relevant kontekst</strong> — samtalerne bruges udelukkende til at forbedre svar om Lokalt Innovations hus i Haslev by.</span>
              </li>
              <li className="flex gap-2">
                <span className="text-orange-500 flex-shrink-0">⚠️</span>
                <span><strong>Ikke personlig rådgivning</strong> — del ikke sensitive personoplysninger i chatten.</span>
              </li>
            </ul>
            <p className="text-xs text-gray-500">
              Behandlingsgrundlag: Legitim interesse (GDPR art. 6.1.f) — forbedring af kommunal digital service.
              Dataansvarlig: Zieaz™ Microphones.
            </p>
          </div>
          <div className="flex gap-3 mt-4">
            <button
              onClick={acceptGdpr}
              className="flex-1 py-2.5 rounded-xl text-white text-sm font-medium"
              style={{ backgroundColor: "#2d6a4f" }}
            >
              Accepter og start chat
            </button>
            <button
              onClick={declineGdpr}
              className="px-4 py-2.5 rounded-xl border border-gray-200 text-gray-600 text-sm hover:bg-gray-50"
            >
              Afvis
            </button>
          </div>
        </div>
      )}

      {/* Normal chat UI — only shown after consent */}
      {gdprConsent === true && <>
      {/* Header */}
      <div
        className={`flex items-center justify-between px-4 py-3 text-white${!fullscreen ? " cursor-move select-none" : ""}`}
        style={{ backgroundColor: "#2d6a4f" }}
        onMouseDown={!fullscreen ? startDrag : undefined}
      >
        <div className="flex items-center gap-2">
          <span className="text-lg">🌿</span>
          <div>
            <div className="font-semibold text-sm">AI Innovations Assistent</div>
            <div className="relative group/rag inline-block">
              <div className="text-xs opacity-70 cursor-default underline decoration-dotted decoration-white/40">
                {llmModel} · RAG aktiv
              </div>
              {/* Tooltip */}
              <div className="pointer-events-none absolute left-0 top-full mt-2 z-50 w-64 rounded-xl bg-gray-900 text-white text-xs p-3 shadow-xl opacity-0 group-hover/rag:opacity-100 transition-opacity duration-150">
                <p className="font-semibold mb-1">
                  {language === "en" ? "How this assistant works" : "Sådan virker assistenten"}
                </p>
                <ul className="space-y-1 text-white/80 leading-relaxed">
                  <li>
                    <span className="text-green-400 font-medium">{llmModel}</span>
                    {language === "en"
                      ? " — local AI language model running on this server. No data is sent to external services."
                      : " — lokal AI-sprogmodel der kører på denne server. Ingen data sendes til eksterne tjenester."}
                  </li>
                  <li>
                    <span className="text-blue-400 font-medium">RAG</span>
                    {language === "en"
                      ? " (Retrieval-Augmented Generation) — before answering, the AI searches the uploaded climate documents for relevant passages and uses them as context."
                      : " (Retrieval-Augmented Generation) — inden svar søger AI'en i de uploadede klimadokumenter efter relevante afsnit og bruger dem som kontekst."}
                  </li>
                </ul>
                <div className="absolute -top-1.5 left-4 w-3 h-3 bg-gray-900 rotate-45" />
              </div>
            </div>
          </div>
        </div>
        <div className="flex gap-2 items-center">
          <button
            onClick={() => setLanguage(language === "da" ? "en" : "da")}
            className="text-white/80 hover:text-white text-xs px-2 py-1 rounded hover:bg-white/20 font-medium tracking-wide"
            title={language === "da" ? "Switch to English" : "Skift til dansk"}
          >
            {language === "da" ? "🇩🇰 DA" : "🇬🇧 EN"}
          </button>
          {!fullscreen && (
            <button
              onClick={() => setOpen(false)}
              className="text-white/70 hover:text-white w-6 h-6 flex items-center justify-center rounded hover:bg-white/20"
            >
              ✕
            </button>
          )}
        </div>
      </div>

      {/* Mode toggle bar */}
      <div className="flex items-center gap-2 px-3 py-2 bg-gray-50 border-b border-gray-200 flex-wrap">
        {/* Toggle 1: conversation mode */}
        <div className="flex rounded-lg border border-gray-200 overflow-hidden text-xs flex-shrink-0">
          <button
            onClick={() => setConversationMode("continuous")}
            className={`px-2.5 py-1.5 transition-colors ${
              conversationMode === "continuous"
                ? "bg-green-700 text-white font-medium"
                : "bg-white text-gray-600 hover:bg-gray-100"
            }`}
            title={language === "en" ? "Keep conversation context" : "Bevar samtale-kontekst"}
          >
            {language === "en" ? "💬 Conversation" : "💬 Samtale"}
          </button>
          <button
            onClick={() => setConversationMode("single")}
            className={`px-2.5 py-1.5 border-l border-gray-200 transition-colors ${
              conversationMode === "single"
                ? "bg-green-700 text-white font-medium"
                : "bg-white text-gray-600 hover:bg-gray-100"
            }`}
            title={language === "en" ? "Each question is answered independently" : "Hvert spørgsmål besvares uafhængigt"}
          >
            {language === "en" ? "❓ Single" : "❓ Enkelt"}
          </button>
        </div>

        {/* Toggle 2: document scope */}
        <div className="flex rounded-lg border border-gray-200 overflow-hidden text-xs flex-shrink-0">
          <button
            onClick={() => setDocScope("all")}
            className={`px-2.5 py-1.5 transition-colors ${
              docScope === "all"
                ? "bg-blue-600 text-white font-medium"
                : "bg-white text-gray-600 hover:bg-gray-100"
            }`}
            title={language === "en" ? "Search all documents" : "Søg i alle dokumenter"}
          >
            {language === "en" ? "📚 All docs" : "📚 Alle"}
          </button>
          <button
            onClick={() => setDocScope("current")}
            disabled={!selectedDocId}
            className={`px-2.5 py-1.5 border-l border-gray-200 transition-colors ${
              docScope === "current" && selectedDocId
                ? "bg-blue-600 text-white font-medium"
                : !selectedDocId
                ? "bg-white text-gray-300 cursor-not-allowed"
                : "bg-white text-gray-600 hover:bg-gray-100"
            }`}
            title={
              !selectedDocId
                ? (language === "en" ? "Open a document in the PDF viewer first" : "Åbn et dokument i PDF-visningen først")
                : (language === "en" ? `Search only: ${selectedDocTitle}` : `Søg kun i: ${selectedDocTitle}`)
            }
          >
            {language === "en" ? "📄 This doc" : "📄 Dette dok."}
          </button>
        </div>

        {/* Clear chat button */}
        <button
          onClick={clearChat}
          className="ml-auto text-xs px-2.5 py-1.5 rounded-lg border border-gray-200 text-gray-500 hover:bg-red-50 hover:text-red-600 hover:border-red-200 transition-colors flex-shrink-0"
          title={language === "en" ? "Clear conversation" : "Ryd samtale"}
        >
          🗑️ {language === "en" ? "Clear" : "Ryd"}
        </button>
      </div>

      {/* Active doc scope indicator */}
      {docScope === "current" && selectedDocId && (
        <div className="bg-blue-50 border-b border-blue-200 px-4 py-1.5 text-xs text-blue-700 flex items-center justify-between">
          <span>📄 {language === "en" ? "Searching in:" : "Søger i:"} <strong className="font-semibold">{selectedDocTitle || selectedDocId}</strong></span>
          <button onClick={() => setDocScope("all")} className="text-blue-500 hover:text-blue-700 ml-2 text-xs">✕</button>
        </div>
      )}

      {/* Selected text banner */}
      {selectedText && (
        <div className="bg-green-50 border-b border-green-200 px-4 py-2 text-xs text-green-800">
          {editingSelectedText ? (
            <div className="flex flex-col gap-1.5">
              <div className="flex items-center justify-between">
                <span className="font-medium">📝 {language === "en" ? "Edit captured text" : "Rediger markeret tekst"}</span>
                <button
                  onClick={() => setEditingSelectedText(false)}
                  className="text-green-600 hover:text-green-800 ml-2"
                  title={language === "en" ? "Cancel" : "Annuller"}
                >✕</button>
              </div>
              <textarea
                autoFocus
                value={editableText}
                onChange={(e) => setEditableText(e.target.value)}
                className="w-full resize-none rounded-lg border border-green-300 bg-white px-2 py-1.5 text-xs text-green-900 focus:outline-none focus:border-green-500"
                rows={3}
              />
              <div className="flex gap-2 justify-end">
                <button
                  onClick={() => {
                    setSelectedText("");
                    setEditingSelectedText(false);
                  }}
                  className="text-xs px-2 py-1 rounded-lg border border-green-300 text-green-700 hover:bg-green-100"
                >
                  {language === "en" ? "Remove" : "Fjern"}
                </button>
                <button
                  onClick={() => {
                    setSelectedText(editableText.trim());
                    setEditingSelectedText(false);
                  }}
                  className="text-xs px-2 py-1 rounded-lg bg-green-700 text-white hover:bg-green-800"
                >
                  {language === "en" ? "Save" : "Gem"}
                </button>
              </div>
            </div>
          ) : (
            <div className="flex items-center justify-between">
              <span className="truncate flex-1">📝 {language === "en" ? "Captured" : "Markeret"}: &quot;{selectedText.slice(0, 80)}{selectedText.length > 80 ? "..." : ""}&quot;</span>
              <div className="flex items-center gap-1 ml-2 flex-shrink-0">
                <button
                  onClick={() => { setEditableText(selectedText); setEditingSelectedText(true); }}
                  className="text-green-600 hover:text-green-800 px-1"
                  title={language === "en" ? "Edit" : "Rediger"}
                >✏️</button>
                <button
                  onClick={() => setSelectedText("")}
                  className="text-green-600 hover:text-green-800 px-1"
                  title={language === "en" ? "Remove" : "Fjern"}
                >✕</button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {messages.length === 0 && (
          <div className="text-center text-gray-500 py-6">
            <div className="text-4xl mb-3">🌱</div>
            <p className="font-medium text-gray-700">
              {language === "en" ? "AI Innovations Assistant" : "AI Innovations Assistent"}
            </p>
            <p className="text-sm mt-2">
              {language === "en" ? (
                <>Ask me about Lokalt Innovations hus i Haslev by,<br />events, workshops or our programs.</>
              ) : (
                <>Stil mig spørgsmål om Lokalt Innovations hus i Haslev by,<br />forestillinger, workshops eller vores programmer.</>
              )}
            </p>
            <QuestionToolbox />
          </div>
        )}

        {messages.map((msg, i) => (
          <div
            key={i}
            className={`chat-message flex ${msg.role === "user" ? "justify-end" : "justify-start"}`}
          >
            {msg.role === "assistant" && (
              <div className="w-7 h-7 rounded-full flex items-center justify-center text-sm mr-2 flex-shrink-0 mt-1"
                style={{ backgroundColor: "#2d6a4f20" }}>
                🌿
              </div>
            )}
            <div
              className={`max-w-[80%] px-4 py-2.5 rounded-2xl text-sm leading-relaxed ${
                msg.role === "user"
                  ? "text-white rounded-tr-sm"
                  : "bg-gray-100 text-gray-800 rounded-tl-sm"
              }`}
              style={msg.role === "user" ? { backgroundColor: "#2d6a4f" } : {}}
            >
              {msg.role === "assistant" && msg.content === "" && loading ? (
                <span className="flex gap-1 py-1">
                  <span className="typing-dot w-2 h-2 rounded-full bg-gray-400 inline-block" />
                  <span className="typing-dot w-2 h-2 rounded-full bg-gray-400 inline-block" />
                  <span className="typing-dot w-2 h-2 rounded-full bg-gray-400 inline-block" />
                </span>
              ) : msg.role === "assistant" ? (
                <>
                <ReactMarkdown
                  components={{
                    p: ({ children }) => <p className="mb-2 last:mb-0">{children}</p>,
                    strong: ({ children }) => <strong className="font-semibold">{children}</strong>,
                    em: ({ children }) => <em className="italic">{children}</em>,
                    ul: ({ children }) => <ul className="list-disc pl-4 mb-2 space-y-0.5">{children}</ul>,
                    ol: ({ children }) => <ol className="list-decimal pl-4 mb-2 space-y-0.5">{children}</ol>,
                    li: ({ children }) => <li className="text-sm">{children}</li>,
                    h1: ({ children }) => <h1 className="text-base font-bold mb-1 mt-2">{children}</h1>,
                    h2: ({ children }) => <h2 className="text-sm font-bold mb-1 mt-2">{children}</h2>,
                    h3: ({ children }) => <h3 className="text-sm font-semibold mb-1 mt-1">{children}</h3>,
                    code: ({ children }) => <code className="bg-gray-200 rounded px-1 text-xs font-mono">{children}</code>,
                    blockquote: ({ children }) => <blockquote className="border-l-2 border-gray-400 pl-3 italic text-gray-600 my-1">{children}</blockquote>,
                    hr: () => <hr className="my-2 border-gray-300" />,
                  }}
                >
                  {msg.content}
                </ReactMarkdown>
                {msg.sources && msg.sources.length > 0 && (
                  <SourceCards sources={msg.sources} language={language} />
                )}
                </>
              ) : (
                <div className="whitespace-pre-wrap">{msg.content}</div>
              )}
            </div>
          </div>
        ))}
        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      <div className="border-t border-gray-200 p-3">
        <div className="flex gap-2 items-end">
          <textarea
            ref={inputRef}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={language === "en" ? "Ask a question... (Enter to send)" : "Stil et spørgsmål... (Enter for at sende)"}
            className="flex-1 resize-none rounded-xl border border-gray-200 px-3 py-2.5 text-sm focus:outline-none focus:border-green-400 max-h-32"
            rows={1}
            style={{ minHeight: 40 }}
          />
          <button
            onClick={sendMessage}
            disabled={loading || !input.trim()}
            className="w-10 h-10 rounded-xl text-white flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed transition-opacity hover:opacity-90 flex-shrink-0"
            style={{ backgroundColor: "#2d6a4f" }}
          >
            {loading ? "⏳" : "→"}
          </button>
        </div>
      </div>
      </>}
    </div>
  );

  if (fullscreen) {
    return (
      <div className="flex-1 max-w-4xl mx-auto w-full px-4 pb-4">
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden h-full">
          {chatContent}
        </div>
      </div>
    );
  }

  return (
    <div
      ref={floatContainerRef}
      className="z-50"
      style={
        floatPos
          ? { position: "fixed", left: floatPos.x, top: floatPos.y, width: floatSize.w, height: floatSize.h }
          : { position: "fixed", bottom: 24, right: 24, width: floatSize.w, height: floatSize.h }
      }
    >
      <div className="rounded-2xl shadow-2xl border border-gray-200 overflow-hidden h-full">
        {chatContent}
      </div>
      {/* Resize handle */}
      <div
        onMouseDown={startResize}
        title="Tilpas størrelse"
        className="absolute bottom-0 right-0 w-5 h-5 cursor-se-resize z-10 flex items-end justify-end pb-1 pr-1"
      >
        <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
          <path d="M2 9L9 2M5 9L9 5M8 9L9 8" stroke="#94a3b8" strokeWidth="1.5" strokeLinecap="round" />
        </svg>
      </div>
    </div>
  );
}

